import { ServerError, useLazyQuery, useQuery } from '@apollo/client';
import { Player } from '@lottiefiles/react-lottie-player';
import * as stripe from '@stripe/stripe-js';
import axios from 'axios';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { DateTime } from 'luxon';
import React, { FC, useContext, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';

import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import {
	IInsuranceOrderSummaryStepPageContent,
	InsuranceOrderSummaryStep,
} from '@ui/routes/Insurance/InsuranceOrderSummaryStep';

import { IAuthenticatedMember, SubscriptionInfo } from '@hooks/useMemberInfo';

import { keysToCamel } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';
import { doesNotExist, exists } from '@utils/general';
import { combineMemberFragments } from '@utils/graphql';

import { useAuth } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';
import { IVisitPriceResponse } from '@folx-types/insurance';

import PageLoader from '@assets/lottie/PageLoading.json';

import { membership, membershipName, queryName } from '@graphql/fragments/membership';
import visitPriceQuery from '@graphql/queries/visitPrice.query.gql';

interface IGahtFuOrderSummaryStepPageProps extends IFlowStepProps {}

const GahtFuOrderSummaryStepPage: FC<IGahtFuOrderSummaryStepPageProps> = ({ path, stepKey }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "insurance-order-summary-step" }) {
				visit_header
				in_network_header
				out_of_network_header
				out_of_network_description
				pay_now_text
				expect_later_text
				ouf_of_pocket_text
				copay_text
				copay_description
				deposit_text
				deposit_description
				subsequent_appointment_text
				initial_appointment_text
				initial_appointment_description
				checkout_text
				loading_header
				appointment_error_header
				invalid_appointment_text
				invalid_appointment_button_text
				past_cancel_date_appointment_text
				reactivate_membership_label
				assignment_of_benefits
			}
		}
	`);

	const maxRefetchCount = 3;

	const [refetchCount, setRefetchCount] = useState(0);
	const [invalidAppointmentError, setInvalidAppointmentError] = useState(false);
	const [pastCancelDateAppointmentError, setPastCancelDateAppointmentError] = useState(false);

	const { email, getAuthToken } = useAuth();
	const { existingFormData, handleFormSubmission, handleNavBack, destinationFlow, submitFormData } =
		useContext(FlowContext);

	// Member Insurance
	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const [getVisitPrice, { data: visitData, error: visitError, loading: visitLoading }] =
		useLazyQuery<IVisitPriceResponse>(visitPriceQuery, {
			client: graphQLv2Client,
		});

	const {
		data: membershipData,
		loading: membershipLoading,
		error: membershipError,
	} = useQuery<IAuthenticatedMember<SubscriptionInfo>>(
		combineMemberFragments([membershipName], [membership], queryName),
		{
			client: graphQLv2Client,
		}
	);

	useDeepCompareEffect(() => {
		if (doesNotExist(destinationFlow) || doesNotExist(existingFormData)) {
			return;
		}

		getVisitPrice({
			variables: {
				serviceType: destinationFlow,
				insuranceId: exists(existingFormData?.memberInsuranceId)
					? existingFormData?.memberInsuranceId
					: null,
			},
		});
	}, [destinationFlow, existingFormData]);

	useDeepCompareEffect(() => {
		if (
			membershipLoading ||
			doesNotExist(existingFormData?.cogsworthAppointmentTime) ||
			doesNotExist(membershipData?.authenticatedMember?.membershipSubscription?.cancelAt)
		) {
			return;
		}

		const cancelAt = membershipData?.authenticatedMember?.membershipSubscription?.cancelAt ?? '';
		const cancelDate = DateTime.fromISO(cancelAt);
		const appointmentDate = DateTime.fromISO(existingFormData?.cogsworthAppointmentTime);
		const isPastCancelDate = appointmentDate > cancelDate;
		setPastCancelDateAppointmentError(isPastCancelDate);
	}, [existingFormData, membershipLoading, membershipData]);

	useDeepCompareEffect(() => {
		if (doesNotExist(visitError)) {
			return;
		}

		const networkError = visitError?.networkError as ServerError;
		const statusCode = networkError?.statusCode;
		if (statusCode !== 504) {
			setRefetchCount(maxRefetchCount);
			return;
		}

		getVisitPrice({
			variables: {
				serviceType: destinationFlow,
				insuranceId: exists(existingFormData?.memberInsuranceId)
					? existingFormData?.memberInsuranceId
					: null,
			},
		});
		setRefetchCount(refetchCount + 1);
	}, [visitError]);

	const visitPrice = visitData?.authenticatedMember?.visitPrice;

	// Stripe Session
	const [loadingStripe, setLoadingStripe] = useState(false);

	const submit = async (assignmentOfBenefits: boolean) => {
		try {
			setLoadingStripe(true);
			const pubKey = email.toLowerCase().endsWith('@folxhealth.com')
				? process.env.GATSBY_STRIPE_TEST_PUBLISHABLE_KEY
				: process.env.GATSBY_STRIPE_PUBLISHABLE_KEY;
			const stripeClient = await stripe.loadStripe(pubKey!);

			const authorizationToken = await getAuthToken();

			const pricingRequest: {
				service_type: string;
				cogsworth_visit_id: string;
				member_insurance_info_id?: string;
				flow_session_uuid: string;
				redirect_host?: string;
			} = {
				service_type: destinationFlow,
				cogsworth_visit_id: existingFormData?.cogsworthId,
				member_insurance_info_id: exists(existingFormData?.memberInsuranceId)
					? existingFormData?.memberInsuranceId
					: null,
				flow_session_uuid: existingFormData.formUuid,
				...(window?.location && {
					redirect_host: `${window.location.protocol}//${window.location.host}`,
				}),
			};
			let stripeSessionId;
			try {
				const response = await axios.post(`${ApiEndpoints.stripe}/visit-session`, pricingRequest, {
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
						authorizationToken,
					},
				});
				stripeSessionId = response.data.checkoutSessionId;
			} catch (error) {
				const message = (error as { response: { data: { message: string } } }).response.data
					.message;
				if (message === 'ERROR_NO_ACTIVE_SUBSCRIPTION_FOUND') {
					navigate('/app/member/reactivate_membership', {
						state: { source: 'product-flow' },
						replace: true,
					});
				}
				setInvalidAppointmentError(true);
				setLoadingStripe(false);
				return;
			}
			await submitFormData({ stripeSessionId, assignmentOfBenefits });

			setLoadingStripe(false);
			await stripeClient?.redirectToCheckout({
				sessionId: stripeSessionId,
			});
		} catch (e) {
			setLoadingStripe(false);
			console.log(`error: ${JSON.stringify(e)}`, e);
		}
	};

	// Component Return
	const refinedContent: IInsuranceOrderSummaryStepPageContent = keysToCamel(
		pageContent
	) as IInsuranceOrderSummaryStepPageContent;

	const loading = visitLoading || membershipLoading;

	const loadingScreen = (
		<CenteredGridItem>
			<FlexColumn gap="lg">
				<HeadingText>{refinedContent?.loadingHeader}</HeadingText>
				<Player autoplay loop src={PageLoader} />
			</FlexColumn>
		</CenteredGridItem>
	);

	return (
		<LoadingWrapper
			loading={
				(doesNotExist(visitData) && doesNotExist(visitError) && refetchCount < maxRefetchCount) ||
				loading
			}
			skeleton={loadingScreen}>
			<InsuranceOrderSummaryStep
				pageContent={refinedContent}
				path={path}
				stepKey={stepKey}
				existingFormData={existingFormData}
				handleFormSubmission={handleFormSubmission}
				handleNavBack={handleNavBack}
				cancelDate={DateTime.fromISO(
					membershipData?.authenticatedMember?.membershipSubscription?.cancelAt ?? ''
				)}
				visitPrice={visitPrice}
				submitting={loadingStripe}
				submitToStripe={submit}
				invalidAppointmentError={invalidAppointmentError}
				pastCancelDateAppointmentError={pastCancelDateAppointmentError}
			/>
		</LoadingWrapper>
	);
};

export default GahtFuOrderSummaryStepPage;
